import React from 'react';
import { useTranslation } from 'react-i18next';
import { ResponsiveBar } from '@nivo/bar';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import { URLS } from '@wix/da-url';
import { BasicTooltip } from '@nivo/tooltip';
import ChartPanelSkeleton from '../../layout/ChartPanelSkeleton';
import { formatMonth } from '../../../../utils';
import { theme, margin } from '../../constants';
import {
  SubscriptionsChartPointType,
  SubscriptionsStatType,
} from '../../../../../../../../types/subscriptionsSection';

interface Props {
  className?: string;
  chart: SubscriptionsChartPointType[];
  stats?: SubscriptionsStatType;
}

const USD_VALUES_FORMATTING: Intl.NumberFormatOptions = {
  minimumFractionDigits: 0,
  maximumFractionDigits: 1,
};

const IncomeChartPanel: React.FC<Props> = ({ className, chart, stats }) => {
  const { t } = useTranslation();
  const isSSR = typeof window === 'undefined';

  const newData = chart.map(({ startDate, ...rest }) => ({
    startDate,
    month: formatMonth(startDate),
    ...rest,
  }));

  return (
    <ChartPanelSkeleton
      className={className}
      totalTitle={t('widgets.subscriptionsSection.center.total.income')}
      total={stats ? formatDollars(stats.income, USD_VALUES_FORMATTING) : ''}
      manageTitle={t('widgets.subscriptionsSection.center.button.income')}
      manageHref={URLS.EARNINGS}
    >
      {!isSSR && (
        <ResponsiveBar
          data={newData}
          indexBy={`month`}
          keys={[`income`]}
          axisLeft={{ tickValues: 5 }}
          gridYValues={5}
          padding={0.2}
          margin={margin}
          defs={[
            {
              id: 'barGradient',
              type: 'linearGradient',
              colors: [
                { offset: 0, color: '#7579ff' },
                { offset: 100, color: 'rgba(200, 109, 215, 0.41)' },
              ],
            },
          ]}
          fill={[{ match: '*', id: 'barGradient' }]}
          valueFormat={(val: number) =>
            val > 0 ? formatDollars(val, USD_VALUES_FORMATTING) : ''
          }
          tooltip={({ indexValue, value }) => (
            <BasicTooltip id={`${indexValue} - ${value}`} />
          )}
          theme={theme}
        />
      )}
    </ChartPanelSkeleton>
  );
};
IncomeChartPanel.displayName = 'IncomeChartPanel';

export default IncomeChartPanel;
